import { startTransition } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { gql } from 'shared/__generated__/gql';
import { useAuth } from 'shared/misc/hooks/useAuth';
import CommentItem from './comment-item';
import SkeletonCommentItem from 'shared/misc/components/SkeletonCommentItem';
import { useRecommendationContext } from 'shared/misc/components/by-type/recommendations/providers/useRecommendationContext';
import { Button as TWButton } from 'web2/app/components/button';
import NoComments from 'shared/misc/components/by-type/feed/utils/NoComments';

type Props = {
  recId: string;
  estimatedCommentCount?: number | null;
};

export const CommentsQuery = gql(/* GraphQL */ `
  query getCommentsQuery($first: Int, $after: String, $targetRecId: String!) {
    commentsConnection(
      first: $first
      after: $after
      enableReplies: true
      targetRecId: $targetRecId
    ) @connection(key: "recViewComments") {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...CommentItemWithReplies
        }
      }
    }
  }
`);

const INITIAL_PAGE_LENGTH = 5;
const PAGE_LENGTH = 25;

function CommentsContainer({ recId, estimatedCommentCount }: Props) {
  const auth = useAuth();

  const { data, fetchMore } = useSuspenseQuery(CommentsQuery, {
    variables: { first: INITIAL_PAGE_LENGTH, targetRecId: recId },
    //fetchPolicy: 'cache-first',
    // pollInterval: 120000,
  });

  // const loaderData = useLoaderData({ from: '/rec/$recId' });

  // const data = useReadQuery(loaderData.queryRef);

  const getNextPage = async () => {
    if (!data?.commentsConnection.pageInfo.hasNextPage) return;
    startTransition(() => {
      fetchMore({
        variables: {
          targetRecId: recId,
          first: PAGE_LENGTH,
          after: data?.commentsConnection.pageInfo.endCursor,
        },
      });
    });
  };

  const commentsConnection = data?.commentsConnection;

  const ctx = useRecommendationContext();

  return (
    <>
      <div className="flex flex-col gap-6 w-full" suppressHydrationWarning>
        {commentsConnection ? (
          <>
            {data?.commentsConnection?.edges?.map(({ node }) => (
              <CommentItem comment={node} key={node.id} />
            ))}
            {commentsConnection.edges.length < ctx.rec.commentsCount! &&
              commentsConnection.pageInfo.hasNextPage && (
                <TWButton
                  variant="textOnly"
                  onClick={() => getNextPage()}
                  className="flex justify-center self-center"
                >
                  Show more ({ctx.rec.commentsCount! - commentsConnection.edges.length})
                </TWButton>
              )}
          </>
        ) : (
          <>
            {(estimatedCommentCount || 0) !== 0 ? (
              <>
                {[...Array(Math.min(estimatedCommentCount!, 5))].map((_, index) => (
                  <SkeletonCommentItem key={index} />
                ))}
              </>
            ) : null}
          </>
        )}
      </div>
      {!ctx.rec.commentsCount && !commentsConnection?.edges?.length && <NoComments />}
    </>
  );
}

export default CommentsContainer;
